<template>
  <ul
    v-if="detailed"
    class="grid gap-8 sm:grid-cols-2 lg:grid-cols-4"
  >
    <li
      v-for="benefit in benefits"
      :key="benefit.key"
      class="flex gap-4"
    >
      <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-50 text-lg text-primary">
        <Icon
          :icon="benefit.icon"
          prefix="far"
        />
      </span>
      <div>
        <strong class="block font-semibold text-heading">{{ benefit.detailedTitle ? $t(benefit.detailedTitle.key, benefit.detailedTitle.text) : $t(benefit.key, benefit.text) }}</strong>
        <p class="mt-1 text-sm text-supplement">
          {{ $t(benefit.descriptionKey, benefit.description).replace(':domain', domain ?? '') }}
        </p>
      </div>
    </li>
  </ul>

  <ul
    v-else
    class="space-y-4"
  >
    <li
      v-for="benefit in benefits"
      :key="benefit.key"
      class="flex items-center space-x-4"
    >
      <span class="flex w-6 shrink-0 justify-center text-xl text-primary">
        <Icon
          :icon="benefit.icon"
          prefix="far"
        />
      </span>
      <span>{{ $t(benefit.key, benefit.text) }}</span>
    </li>
  </ul>
</template>

<script setup lang="ts">
import type { IconName } from '@fortawesome/fontawesome-svg-core'

const props = defineProps<{
  isBrandVoice?: boolean
  // Adds a description to each benefit, written in the brand's voice
  detailed?: boolean
  domain?: string
}>()

type Benefit = {
  key: string
  text: string
  // A shorter title for when there's a description to say the rest
  detailedTitle?: { key: string, text: string }
  icon: IconName
  descriptionKey: string
  description: string
}

const benefits = computed<Benefit[]>(() => [
  {
    ...(props.isBrandVoice
      ? { key: 'copilot_benefit_brand_ours', text: 'Your brand on our website' }
      : { key: 'copilot_benefit_brand', text: 'Your brand on their website' }),
    icon: 'id-card',
    descriptionKey: 'copilot_benefit_brand_description',
    description: 'Your logo and contact details appear on every page.'
  },
  {
    key: 'copilot_benefit_share',
    text: 'Share any page with your clients',
    detailedTitle: { key: 'copilot_benefit_share_short', text: 'Share any page' },
    icon: 'share-nodes',
    descriptionKey: 'copilot_benefit_share_description',
    description: 'Personalize and share any page on :domain.'
  },
  {
    key: 'copilot_benefit_pricing',
    text: 'Live pricing & availability',
    icon: 'arrows-rotate',
    descriptionKey: 'copilot_benefit_pricing_description',
    description: 'Always up to date, straight from our official website.'
  },
  {
    key: 'copilot_benefit_leads',
    text: 'Every lead comes directly to you',
    detailedTitle: { key: 'copilot_benefit_leads_short', text: 'Every lead comes to you' },
    icon: 'bullseye-arrow',
    descriptionKey: 'copilot_benefit_leads_description',
    description: 'Inquiries go straight to your inbox, not ours.'
  }
])
</script>
